/* eslint-disable @typescript-eslint/no-explicit-any */
import { DCA_CONSTANTS, PPV5_URL_FORMAT } from '../constants';
import { getToken } from './localeToken';

type LoginState = 'authenticated' | 'remembered';

/**
 * Configuration interface for customizing DCA payload generation.
 * Apps can provide their own configuration to customize behavior without
 * requiring app-specific code in this shared library.
 */
export interface DCAPayloadConfig {
  /** DCA channel identifier - defaults to 'WEB' */
  channel?: 'GRE' | 'WEB';

  /** Custom parameters to include in the payload (replaces default parameters when provided) */
  customParameters?: Array<{ name: string; value: string }>;

  /** Whether to include web context (URL, referrer) in the payload - defaults to true */
  includeWebContext?: boolean;

  /** Whether to include target property token overrides - defaults to true */
  includeTargetOverrides?: boolean;

  /** Custom identity configuration */
  identity?: {
    /** Returns the alternate customer ID if available */
    getAltCustomerId?: () => string | undefined;
    /** Returns the session ID for unauthenticated users */
    getSessionId?: () => string;
    /** Authentication state to use - defaults to 'AUTHENTICATED' */
    authenticatedState?: string;
    /** Fallback authentication state when no altCustomerId - defaults to 'AMBIGUOUS' */
    fallbackAuthenticatedState?: string;
  };
}

export const getTimezoneOffsetMinutes = () => (typeof window !== 'undefined' ? -new Date().getTimezoneOffset() : 0);

export const getCookieValue = (cookieName: string): string => {
  if (typeof document === 'undefined') return '';
  const cookies = document.cookie.split('; ');
  for (const cookie of cookies) {
    const [key, value] = cookie.split('=');
    if (key === cookieName) {
      return value || '';
    }
  }
  return '';
};

export function targetPayloadRequestV2(
  req: any,
  datalayer: Record<string, unknown> = window?.['dataLayer'] || {},
  mvpOffers: any = window?.['mvpOffers'] || {},
  brandBannerParams: Record<string, unknown>[] = [],
  config?: DCAPayloadConfig
) {
  const channel = config?.channel ?? 'WEB';
  const includeWebContext = config?.includeWebContext ?? true;
  const includeTargetOverrides = config?.includeTargetOverrides ?? true;

  const satellite = window?._satellite;
  const pageHref = window?.location?.href;
  const targetParameters = targetRequestPayload([
    'env_is_prod',
    'env_site_id',
    'env_platform',
    'env_site_name',
    'env_server_id',
    'mr_prof_authentication_state',
    'search_advance_purchase_days',
    'prop_address_city_state_country',
    'search_google_places_destination',
    'country',
    'canBuyPoints',
    'daysSinceJoining',
    'daysToCheckin',
    'miRecentlyViewedProperties',
    'mr_prof_upcoming_stay_consolidated',
    'search_cluster_code',
    'page_url_query_string',
    'cookie_merchViewed',
    'firstTimeUserCopy',
    'rpcCode',
    'offerCode',
    'specialMessage',
    'memState',
    'invokeFlushErrors',
    'device_language_preferred',
    'page_url_path_branch',
    'browser_akamai_loc_country',
    'ensighten_settings',
    'browser_akamai_loc_long',
    'rk_enabled',
    'page_requested_uri',
    'env_img_path',
    'prof_address_state',
    'env_jacket',
    'session_jsession_id',
    'cookie_mi_site',
    'sessionId',
    'mr_isLinkedUser',
    'browser_akamai_loc_lat',
    'env_date_time',
    'mvp_prequal_endpoint',
    'brwsrLang',
    'page_domain_name',
    'cookie_mi_visitor',
    'request_id',
    'page_data_layer_ready',
    'previous_page',
    'browser_akamai_loc_state',
    'mr_prof_cc_all',
    'mr_prof_address_country',
    'browser_akamai_loc_city',
    'mr_prof_nights_to_next_level',
    'mr_prof_points_balance',
    'mr_prof_member_program_level',
    'mvpOffers_source',
    'promoIDsRegistered',
    'promoIDsEligible',
    'mr_prof_rewards_level',
    'mr_prof_address_state',
    'userAgent',
    ...(pageHref?.includes(PPV5_URL_FORMAT) ? ['prop_marsha_code', 'prop_brand_code', 'prop_brand_tier'] : []),
  ]);

  function getIdentitiesObject() {
    const loginState: LoginState = datalayer['memState'] as LoginState;
    const isAuthenticated = loginState === 'authenticated' || loginState === 'remembered';
    if (isAuthenticated && (datalayer['altCustId'] || datalayer['mr_id_alternate'])) {
      return {
        identities: [
          {
            namespace: {
              code: 'altcustid',
            },
            primary: true,
            authenticatedState: DCA_CONSTANTS[loginState],
            id: datalayer['altCustId'] || datalayer['mr_id_alternate'],
          },
        ],
      };
    } else return {};
  }

  function targetRequestPayload(variables: any) {
    const result: any = {};
    const parameters = variables.map((variable: string) => {
      let value: any = ''; // Default value if no valid value found
      const getSatelliteVar = satellite?.['getVar'];
      if (satellite && satellite?.['getVar'] && getSatelliteVar(variable)?.length) {
        value = getSatelliteVar(variable);
      } else if (Array.isArray(datalayer?.[variable]) || typeof datalayer?.[variable] === 'string') {
        value = datalayer[variable];
      }
      if (variable === 'mvpOffers_source' && mvpOffers?.source) {
        value = mvpOffers.source;
      }
      if (variable === 'mr_prof_authentication_state') {
        value = value || 'unauthenticated'; // Set default value 'unauthenticated' if value is falsy
      }
      if (variable === 'userAgent') {
        value = window?.navigator?.userAgent;
      }

      if (variable === 'previous_page') {
        value = value ? value : '';
        datalayer[variable] = value ? value : '';
      }
      result[variable] = value;
      return { name: variable, value: value };
    });
    return parameters;
  }

  /**
   *
   * @returns list of mbox parameters with the required properties
   * NOTE: we dont need to copy & pass parameters for all the mboxes, we can include it once  for all the moboxes
   * i.e., name, localeOfContent, parameters
   */
  function constructMboxPayloadParams() {
    return (req?.mboxParameters as Record<string, string>[])?.map(item => {
      return {
        name: item?.['mboxName'],
        attributes: [
          { name: 'trackingDescription', value: DCA_CONSTANTS.DCA },
          { name: 'trackingContentPosition', value: item?.['mboxName']?.split('-mbox')?.[0] },
          { name: 'trackingDisplayCode', value: item?.['cellCodes'] },
        ],
      };
    });
  }

  function constructRecentlyViewedPropertiesPayload(): any {
    const isRecentlyViewedPropertiesAvailable =
      typeof window !== 'undefined' ? localStorage.getItem('miRecentlyViewedProperties') : null;
    if (!isRecentlyViewedPropertiesAvailable) {
      return [];
    }
    let miRecentlyViewedProperties: any = [];
    try {
      miRecentlyViewedProperties = JSON.parse(isRecentlyViewedPropertiesAvailable)?.recentlyViewedProperties?.[0] || {};
    } catch (e) {
      miRecentlyViewedProperties = [];
    }
    return [
      {
        name: 'rv_name',
        value: miRecentlyViewedProperties?.name ?? '',
      },
      {
        name: 'rv_masha',
        value: miRecentlyViewedProperties?.marshaCode ?? '',
      },
      {
        name: 'rv_assetId',
        value: window?.btoa(miRecentlyViewedProperties?.imageSrc ?? '') ?? '',
      },
      {
        name: 'rv_eyebrow',
        value: [miRecentlyViewedProperties?.state, miRecentlyViewedProperties?.country].filter(Boolean).join(', '),
      },
      {
        name: 'rv_recently_viewed',
        value: 'Recently Viewed',
      },
      {
        name: 'rv_description',
        value: 'Pick up where you left off! Continue exploring this property.',
      },
    ];
  }

  const mboxes = constructMboxPayloadParams();

  // Build parameters: use custom parameters if provided, otherwise use default target parameters
  const parameters = config?.customParameters ?? [
    ...targetParameters,
    ...brandBannerParams,
    ...constructRecentlyViewedPropertiesPayload(),
  ];

  // Build context based on configuration
  const context: Record<string, any> = {
    channel,
    overrides: {},
    xdm: {
      placeContext: {
        localTimezoneOffset: getTimezoneOffsetMinutes(),
      },
    },
  };

  // Add target overrides if enabled (default behavior for WEB channel)
  if (includeTargetOverrides) {
    context['overrides'] = {
      target: [
        {
          name: 'propertyToken',
          value: getToken(req?.locale?.replace('_', '-'), String(datalayer?.['env_is_prod']) === 'true'),
        },
      ],
    };
  }

  // Add web context if enabled (default behavior for WEB channel)
  if (includeWebContext) {
    context['xdm'].web = {
      webPageDetails: {
        URL: window?.location?.href,
      },
      webReferrer: {
        URL: window?.location?.origin,
      },
    };
    // Use default identity logic for web apps
    Object.assign(context['xdm'], getIdentitiesObject());
  }

  // Handle custom identity configuration (for apps like GRT that need different identity handling)
  if (config?.identity) {
    const { getAltCustomerId, getSessionId, authenticatedState, fallbackAuthenticatedState } = config.identity;
    const altCustomerId = getAltCustomerId?.();
    const sessionId = getSessionId?.();

    if (altCustomerId) {
      context['xdm'].identities = [
        {
          authenticatedState: authenticatedState ?? 'AUTHENTICATED',
          id: altCustomerId,
          namespace: {
            code: 'altcustid',
          },
          primary: true,
        },
      ];
    } else if (sessionId) {
      context['xdm'].identities = [
        {
          authenticatedState: fallbackAuthenticatedState ?? 'AMBIGUOUS',
          id: sessionId,
          namespace: {
            code: 'sessionid',
          },
          primary: true,
        },
      ];
    }
  }

  return {
    localeOfContent: req.locale,
    parameters,
    mboxes,
    context,
  };
}
